import { useEffect, useState } from "react";
import Navigation from "./components/Navigation";
import Hero from "./sections/Hero";
import HomeIntro from "./sections/HomeIntro";
import Clients from "./sections/Clients";
import Contact from "./sections/Contact";
import Cart from "./sections/Cart";
import Team from "./sections/Team";
import Portal from "./sections/Portal";
import SkeulurStatus from "./sections/SkeulurStatus";
import Careers from "./sections/Careers";
import Footer from "./sections/Footer";
import InfoPage from "./sections/InfoPage";
import { infoPages, type InfoPageKey } from "./data/infoPages";

export type Page =
  | "home"
  | "contact"
  | "team"
  | "cart"
  | "portal"
  | "skeulur-status"
  | "careers"
  | InfoPageKey;

// File name (without .html) -> page, for the pages built from data/infoPages.ts.
const infoByFile = Object.fromEntries(
  (Object.keys(infoPages) as InfoPageKey[]).map((k) => [
    infoPages[k].file.replace(/\.html$/, ""),
    k,
  ]),
) as Record<string, InfoPageKey>;

const OWN_PAGES: Page[] = ["contact", "team", "cart", "portal", "skeulur-status", "careers"];

// Match the exact file name so a page like contractor-websites.html can't be
// mistaken for another page by a substring match.
export const pageFromPath = (pathname: string): Page => {
  const file = (pathname.split("/").pop() ?? "").replace(/\.html$/, "");
  if ((OWN_PAGES as string[]).includes(file)) return file as Page;
  return infoByFile[file] ?? "home";
};

// initialPage lets the server (see entry-server.tsx) render the right page
// for each of the static HTML files. main.tsx never passes this prop on
// the client (a real browser load has no way to know it ahead of the first
// render either), so the client independently computes the same value from
// window.location.pathname -- but it must do that SYNCHRONOUSLY, in the
// useState initializer, not in a useEffect. Effects run after the first
// commit, so by the time an effect could call setPage, React has already
// hydrated against a first client render that (without this) defaulted to
// "home" and mismatched whatever page the server actually sent, throwing a
// hydration error on every page except the real homepage.
function App({ initialPage }: { initialPage?: Page } = {}) {
  const [scrolled, setScrolled] = useState(false);
  const [page] = useState<Page>(
    () =>
      initialPage ??
      (typeof window !== "undefined"
        ? pageFromPath(window.location.pathname)
        : "home"),
  );

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 overflow-x-clip">
      <Navigation scrolled={scrolled} page={page} />

      <main>
        {page === "home" && (
          <>
            <Hero>
              <Clients embedded />
            </Hero>
            <HomeIntro />
          </>
        )}
        {page === "contact" && <Contact />}
        {page === "cart" && <Cart />}
        {page === "team" && <Team />}
        {page === "portal" && <Portal />}
        {page === "skeulur-status" && <SkeulurStatus />}
        {page === "careers" && <Careers />}
        {page in infoPages && (
          <InfoPage data={infoPages[page as InfoPageKey]} />
        )}
      </main>

      <Footer />
    </div>
  );
}

export default App;
